import { useState } from "react";
import Reveal from "@/pages/home/components/Reveal";
import SectionTitle from "@/pages/home/components/SectionTitle";
import { comments } from "@/mocks/home";

export default function CommentWall() {
  const [likes, setLikes] = useState<number[]>(() => comments.map((c) => c.likes));
  const [liked, setLiked] = useState<boolean[]>(() => comments.map(() => false));
  const [draft, setDraft] = useState("");
  const [toast, setToast] = useState("");

  const toggleLike = (index: number) => {
    setLikes((prev) => prev.map((v, i) => (i === index ? (liked[index] ? v - 1 : v + 1) : v)));
    setLiked((prev) => prev.map((v, i) => (i === index ? !v : v)));
  };

  const handleSubmit = () => {
    setToast(draft.trim() ? "留言已记录为草稿，正式互动功能上线后即可发布" : "先写点想说的话吧");
    setDraft("");
    window.setTimeout(() => setToast(""), 2600);
  };

  return (
    <section id="talk" className="bg-background-100/70 py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <Reveal>
          <SectionTitle
            kicker="读者讨论"
            title="入口好不好用，读者最有发言权"
            desc="这里收集了大家在实际访问里踩过的坑和亲测有效的做法。看完不妨说说你遇到的是哪一种情况。"
          />
        </Reveal>

        <div className="mt-10 grid grid-cols-1 gap-8 lg:grid-cols-[1.35fr_0.65fr]">
          <div className="flex flex-col gap-4">
            {comments.map((item, index) => (
              <Reveal key={item.name} delay={index * 60}>
                <article className="flex gap-4 rounded-xl border border-background-200/80 bg-background-50 p-5">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary-100 font-heading text-base font-bold text-primary-700">
                    {item.initial}
                  </span>
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                      <span className="font-heading text-sm font-bold text-foreground-950">{item.name}</span>
                      {item.badge ? (
                        <span className="rounded-full bg-accent-100 px-2 py-0.5 text-[10px] font-medium text-accent-800">
                          {item.badge}
                        </span>
                      ) : null}
                      <span className="text-xs text-foreground-500">{item.time}</span>
                    </div>
                    <p className="mt-2 text-sm leading-relaxed text-foreground-700">{item.content}</p>
                    <div className="mt-3 flex items-center gap-4">
                      <button
                        type="button"
                        onClick={() => toggleLike(index)}
                        className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                          liked[index]
                            ? "bg-primary-500 text-background-50"
                            : "bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-800"
                        }`}
                      >
                        <i className={liked[index] ? "ri-heart-fill text-sm" : "ri-heart-line text-sm"} aria-hidden="true"></i>
                        {likes[index]}
                      </button>
                      <span className="inline-flex items-center gap-1.5 text-xs text-foreground-500">
                        <i className="ri-chat-3-line text-sm" aria-hidden="true"></i>
                        回复
                      </span>
                    </div>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>

          <Reveal delay={120}>
            <div className="rounded-xl border border-background-200/80 bg-background-50 p-6 lg:sticky lg:top-28">
              <h3 className="font-heading text-lg font-bold text-foreground-950">说说你遇到的情况</h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                在哪一步卡住的？是加载不出来、跳转异常，还是播放卡顿？一句话说清，后来的人就能少走弯路。
              </p>

              <label htmlFor="talk-input" className="mt-5 block text-xs font-medium text-foreground-700">
                你的留言
              </label>
              <textarea
                id="talk-input"
                name="talk"
                value={draft}
                maxLength={500}
                onChange={(e) => setDraft(e.target.value)}
                rows={4}
                placeholder="例如：晚上八点主站有点挤，切到备用线路就顺了……"
                className="mt-2 w-full rounded-md border border-background-300/80 bg-background-100/60 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
              ></textarea>
              <div className="mt-1 flex items-center justify-between text-[11px] text-foreground-500">
                <span>静态展示，不会真实提交</span>
                <span>{draft.length}/500</span>
              </div>

              <button
                type="button"
                onClick={handleSubmit}
                className="mt-4 inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-send-plane-line text-base" aria-hidden="true"></i>
                提交留言
              </button>

              <div className="mt-6 border-t border-background-200/80 pt-5">
                <p className="text-xs font-medium text-foreground-700">大家最常提到</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {["加载超时", "高峰卡顿", "旧地址失效", "跳转异常"].map((tag) => (
                    <span
                      key={tag}
                      className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-900"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              {toast ? (
                <p className="mt-4 flex items-center gap-2 rounded-md bg-foreground-950 px-3 py-2 text-xs text-background-50">
                  <i className="ri-information-line text-sm text-primary-300" aria-hidden="true"></i>
                  {toast}
                </p>
              ) : null}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}